import { useState, useEffect, useRef } from 'react';

const useVideoAnalysis = ({
    videoRef,
    isRecording,
    faceLandmarker,
    poseLandmarker,
    handLandmarker,
    onAnalysisUpdate,
    // Add processing frequency option
    processingFrequency = 5
}) => {
    // States for feedback indicators
    const [facialEngagement, setFacialEngagement] = useState(0);
    const [handsVisible, setHandsVisible] = useState(false);
    const [goodPosture, setGoodPosture] = useState(false);

    const [facialEngagementProgress, setFacialEngagementProgress] = useState(0);
    const [handsProgress, setHandsProgress] = useState(0);
    const [postureProgress, setPostureProgress] = useState(0);

    const lastVideoTimeRef = useRef(-1);
    const requestAnimationFrameRef = useRef(null);
    const analysisIntervalRef = useRef(null);
    const lastAnalysisTime = useRef(0);
    const frameCountRef = useRef(0);
    const analysisBufferRef = useRef({
        faceExpressions: [],
        poseData: [],
        handGestures: []
    });

    // eslint-disable-next-line
    const workerRef = useRef(null);

    const calculateFacialEngagement = (blendshapes) => {
        if (!blendshapes?.length) return 0;

        // Adjusted weights to make scores more achievable
        const expressionWeights = {
            mouthSmileLeft: 0.5,
            mouthSmileRight: 0.5,
            eyeWideLeft: 0.4,
            eyeWideRight: 0.4,
            jawOpen: 0.4,
            browInnerUp: 0.4,
            eyeBlinkLeft: 0.3,
            eyeBlinkRight: 0.3
        };

        const categories = blendshapes[0]?.categories || [];
        let engagementScore = 0;
        let hasAnyExpression = false;

        categories.forEach(cat => {
            if (expressionWeights[cat.categoryName]) {
                // Consider any score above 0.2 as active expression
                if (cat.score > 0.2) {
                    hasAnyExpression = true;
                }
                engagementScore += cat.score * expressionWeights[cat.categoryName];
            }
        });

        // If face is completely neutral (no expressions), return 0
        if (!hasAnyExpression) {
            return 0;
        }

        // Normalize to 0-100 range with adjusted scaling
        // Multiply by 2.5 to make it easier to reach higher scores
        const normalizedScore = Math.min(100, engagementScore * 250);
        return normalizedScore;
    };

    // Frame analysis effect with throttling
    useEffect(() => {
        if (!isRecording || !faceLandmarker || !poseLandmarker || !handLandmarker || !videoRef.current) return;

        // Set up analysis batch updates at a lower frequency
        analysisIntervalRef.current = setInterval(() => {
            if (analysisBufferRef.current.faceExpressions.length > 0) {
                // Only send the latest data point to reduce payload size
                const latestData = {
                    faceExpressions: [analysisBufferRef.current.faceExpressions[analysisBufferRef.current.faceExpressions.length - 1]],
                    poseData: [analysisBufferRef.current.poseData[analysisBufferRef.current.poseData.length - 1]],
                    handGestures: [analysisBufferRef.current.handGestures[analysisBufferRef.current.handGestures.length - 1]]
                };

                onAnalysisUpdate(latestData);

                // Clear buffer after sending
                analysisBufferRef.current = {
                    faceExpressions: [],
                    poseData: [],
                    handGestures: []
                };
            }
        }, 1000); // Send analysis data every second

        const analyzeFrame = async () => {
            const video = videoRef.current;

            if (!video || video.paused || video.ended) {
                requestAnimationFrameRef.current = requestAnimationFrame(analyzeFrame);
                return;
            }

            // Process only every Nth frame
            frameCountRef.current = (frameCountRef.current + 1) % processingFrequency;

            if (frameCountRef.current !== 0) {
                requestAnimationFrameRef.current = requestAnimationFrame(analyzeFrame);
                return;
            }

            const now = performance.now();

            // Throttle analysis to ~5fps max
            if (now - lastAnalysisTime.current > 200 && video.currentTime !== lastVideoTimeRef.current) {
                lastVideoTimeRef.current = video.currentTime;
                lastAnalysisTime.current = now;

                try {
                    // PERFORMANCE OPTIMIZATION: Only run face detection - the most important feature
                    // and least resource intensive compared to pose and hand detection
                    const faceResults = faceLandmarker.detectForVideo(video, now);

                    // Run pose and hand detection less frequently (every 15 frames)
                    let poseResults = { landmarks: [] };
                    let handResults = { landmarks: [] };

                    if (frameCountRef.current % 15 === 0) {
                        poseResults = poseLandmarker.detectForVideo(video, now);
                        handResults = handLandmarker.detectForVideo(video, now);
                    }

                    // Calculate facial engagement
                    if (faceResults.faceBlendshapes?.length > 0) {
                        const engagementScore = calculateFacialEngagement(faceResults.faceBlendshapes);
                        setFacialEngagement(prevScore => {
                            // Use moving average to smooth out jumps
                            return prevScore * 0.7 + engagementScore * 0.3;
                        });
                    }

                    // Process hands and pose only if we ran detection this frame
                    if (frameCountRef.current % 15 === 0) {
                        // Detect hands with some debouncing
                        const handsDetected = handResults.landmarks?.length > 0;
                        setHandsVisible(prevState => {
                            // Add some stickiness to reduce flicker
                            if (prevState === handsDetected) return prevState;
                            return handsDetected;
                        });

                        if (poseResults.landmarks?.length > 0 && poseResults.landmarks[0]) {
                            const landmarks = poseResults.landmarks[0];
                            const leftShoulder = landmarks[11];
                            const rightShoulder = landmarks[12];

                            const areShouldersDetected = leftShoulder && rightShoulder &&
                                leftShoulder.visibility > 0.5 && rightShoulder.visibility > 0.5 &&
                                typeof leftShoulder.y === 'number' && typeof rightShoulder.y === 'number';

                            if (areShouldersDetected) {
                                const shoulderTilt = Math.abs(leftShoulder.y - rightShoulder.y);
                                const isGoodPosture = shoulderTilt < 0.1;

                                // Add stickiness to posture detection
                                setGoodPosture(prevPosture => {
                                    if (prevPosture === isGoodPosture) return prevPosture;
                                    // Only change state if we have 3 consecutive frames
                                    return isGoodPosture;
                                });
                            }
                        }
                    }

                    // Queue minimal analysis data for batched updates
                    // Store only necessary data to reduce memory usage
                    analysisBufferRef.current.faceExpressions.push({
                        timestamp: now,
                        engagement: facialEngagement
                    });

                    // Add minimal pose/hand data
                    analysisBufferRef.current.poseData.push({
                        timestamp: now,
                        posture: goodPosture
                    });

                    analysisBufferRef.current.handGestures.push({
                        timestamp: now,
                        visible: handsVisible
                    });

                } catch (error) {
                    console.error('Error analyzing frame:', error);
                }
            }

            // Use animation frame for next analysis attempt
            requestAnimationFrameRef.current = requestAnimationFrame(analyzeFrame);
        };

        analyzeFrame();

        return () => {
            if (requestAnimationFrameRef.current) {
                cancelAnimationFrame(requestAnimationFrameRef.current);
            }
            if (analysisIntervalRef.current) {
                clearInterval(analysisIntervalRef.current);
            }
        };
        // eslint-disable-next-line
    }, [isRecording, faceLandmarker, poseLandmarker, handLandmarker, onAnalysisUpdate, videoRef, processingFrequency]);

    // Animate progress bars with RAF instead of setInterval
    useEffect(() => {
        if (!isRecording) return;

        const targetFacialEngagement = facialEngagement;
        const targetHands = handsVisible ? 100 : 0;
        const targetPosture = goodPosture ? 100 : 0;

        let animationFrame;

        const animateProgress = () => {
            // Update facial engagement progress - limit update frequency
            setFacialEngagementProgress(prev => {
                const diff = targetFacialEngagement - prev;
                // Only update if difference is significant
                if (Math.abs(diff) < 3) return prev;
                const step = Math.sign(diff) * Math.min(Math.abs(diff), 3);
                return prev + step;
            });

            // Update hands progress
            setHandsProgress(prev => {
                const diff = targetHands - prev;
                // Only update if difference is significant
                if (Math.abs(diff) < 5) return prev;
                const step = Math.sign(diff) * Math.min(Math.abs(diff), 5);
                return prev + step;
            });

            // Update posture progress
            setPostureProgress(prev => {
                const diff = targetPosture - prev;
                // Only update if difference is significant
                if (Math.abs(diff) < 5) return prev;
                const step = Math.sign(diff) * Math.min(Math.abs(diff), 5);
                return prev + step;
            });

            animationFrame = requestAnimationFrame(animateProgress);
        };

        animationFrame = requestAnimationFrame(animateProgress);

        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [facialEngagement, handsVisible, goodPosture, isRecording]);

    return {
        facialEngagement,
        handsVisible,
        goodPosture,
        facialEngagementProgress,
        handsProgress,
        postureProgress
    };
};

export default useVideoAnalysis;